import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Switch,
  useToast,
} from '@chakra-ui/react';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { useMealPlanShoppingListMacrosEnabled } from '@web-app/utils/feature-flags/meal-plan-shopping-list-macros';
import { capitalizeWord } from '@web-app/utils/format.utils';
import { useMolecule } from 'bunshi/react';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { IconType } from 'react-icons';
import { PiBowlFood, PiCoffee, PiCookie, PiHamburger } from 'react-icons/pi';

import { BerryLoader } from '../BerryLoader';
import { Text } from '../Typography/Text';
import { MealDetail } from './MealDetail';
import { MealPlanStepHeader } from './MealPlanStepHeader';
import { MealPlanView, MEALS, MealType } from './utils';

const MEAL_ICONS: Record<MealType, IconType> = {
  breakfast: PiCoffee,
  lunch: PiHamburger,
  dinner: PiBowlFood,
  snack: PiCookie,
};

export interface DetailedMealPlanProps {
  readOnly: boolean;
}

export const DetailedMealPlan = ({ readOnly }: DetailedMealPlanProps) => {
  const {
    detailedMealPlanAtom,
    isDetailedMealPlanLoadingAtom,
    isMealPlanUpdatingAtom,
    detailedMealPlanEditModeAtom,
    includeMacroDataAtom,
    analyticsContextAtom,
  } = useMolecule(mealPlanMolecule);
  const detailedMealPlan = useAtomValue(detailedMealPlanAtom);
  const isPending = useAtomValue(isDetailedMealPlanLoadingAtom);
  const isMealPlanUpdating = useAtomValue(isMealPlanUpdatingAtom);
  const detailedMealPlanEditMode = useAtomValue(detailedMealPlanEditModeAtom);
  const [includeMacroDataInMealPlan, setIncludeMacroDataInMealPlan] =
    useAtom(includeMacroDataAtom);
  const analyticsContext = useAtomValue(analyticsContextAtom);

  const mealPlanShoppingListMacros = useMealPlanShoppingListMacrosEnabled();

  const [activeDay, setActiveDay] = useState(0);
  const [activeMealType, setActiveMealType] = useState<MealType>('breakfast');

  const toast = useToast({
    position: 'bottom-right',
    isClosable: true,
    duration: 5000,
  });

  const { trackEvent } = useAnalytics();
  useEffect(
    function trackRecipesViewEvent() {
      trackEvent(EVENT_NAMES.VIEW_FULL_MEAL_PLAN_RECIPES, {
        context: analyticsContext,
      });
    },
    [trackEvent, analyticsContext],
  );

  const handleDaySelect = (dayIndex: number) => {
    if (detailedMealPlanEditMode) {
      toast({
        title: 'Please save or cancel current recipe edits',
        status: 'info',
        duration: 3000,
      });
      return;
    }
    setActiveDay(dayIndex);
    trackEvent(EVENT_NAMES.CLICKS_TO_VIEW_SPECIFIC_RECIPE, {
      context: analyticsContext,
    });
  };

  const handleMealSelect = (mealType: MealType) => {
    if (detailedMealPlanEditMode) {
      toast({
        title: 'Please save or cancel current recipe edits',
        status: 'info',
        duration: 3000,
      });
      return;
    }
    setActiveMealType(mealType);
    trackEvent(EVENT_NAMES.CLICKS_TO_VIEW_SPECIFIC_RECIPE, {
      context: analyticsContext,
    });
  };

  if (isPending && !detailedMealPlan) {
    return (
      <Flex
        width="100%"
        minHeight="300px"
        flex={1}
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <BerryLoader />
        <Text>Loading meal plan</Text>
      </Flex>
    );
  }

  if (!detailedMealPlan) return null;

  const dayCount = detailedMealPlan.detailedMealPlan.length;
  const currentDay = detailedMealPlan.detailedMealPlan[activeDay];
  const activeMeal = currentDay?.[activeMealType];
  const showNutritionInfo =
    !!mealPlanShoppingListMacros && includeMacroDataInMealPlan;

  return (
    <Flex flexDir="column" gap={4} position="relative" marginBottom="32px">
      {isMealPlanUpdating && (
        <Flex
          position="absolute"
          top={0}
          left={0}
          zIndex={1}
          bg="rgba(255, 255, 255, 0.5)"
          w="100%"
          h="100%"
        >
          <BerryLoader height="100%" />
        </Flex>
      )}

      <Flex justifyContent="space-between" alignItems="flex-end">
        <MealPlanStepHeader step={MealPlanView.Details} />
        <Flex flexDir="column" alignItems="flex-end" gap={2}>
          {mealPlanShoppingListMacros && !readOnly && (
            <HStack spacing={2}>
              <Text textType="sm">Show nutrition info</Text>
              <Switch
                isChecked={includeMacroDataInMealPlan}
                onChange={(e) =>
                  setIncludeMacroDataInMealPlan(e.target.checked)
                }
                colorScheme="green"
              />
            </HStack>
          )}
          {mealPlanShoppingListMacros && readOnly && (
            <Text textType="sm" color="text.muted">
              Nutrition info{' '}
              {detailedMealPlan.displayNutritionalInformation
                ? 'included'
                : 'not included'}
            </Text>
          )}
        </Flex>
      </Flex>

      {/* Day tabs */}
      <Box mt={4}>
        <HStack spacing={1} overflowX="auto">
          {Array.from({ length: dayCount }, (_, i) => (
            <Button
              key={`day-tab-${i}`}
              size="sm"
              variant={i === activeDay ? 'solid' : 'ghost'}
              onClick={() => handleDaySelect(i)}
              minW="auto"
              px={4}
              fontWeight={i === activeDay ? 600 : 400}
            >
              Day {i + 1}
            </Button>
          ))}
        </HStack>
        <Box borderBottomWidth="1px" borderColor="border.default" mb={6} />
      </Box>

      {/* Meal type cards */}
      <HStack spacing={3} mb={6}>
        {MEALS.map((mealType) => (
          <Flex
            key={mealType}
            flexDir="column"
            alignItems="center"
            justifyContent="center"
            gap={1}
            px={6}
            py={3}
            borderWidth="1px"
            borderColor={
              activeMealType === mealType ? 'green.500' : 'border.default'
            }
            borderRadius="md"
            cursor="pointer"
            bg={activeMealType === mealType ? 'green.50' : 'transparent'}
            onClick={() => handleMealSelect(mealType)}
            flex={1}
            _hover={{ borderColor: 'green.300' }}
          >
            <Icon
              as={MEAL_ICONS[mealType]}
              boxSize={6}
              color={activeMealType === mealType ? 'green.600' : 'text.muted'}
            />
            <Text
              textType="sm"
              fontWeight={activeMealType === mealType ? 600 : 400}
              color={activeMealType === mealType ? 'green.600' : 'text.default'}
            >
              {capitalizeWord(mealType)}
            </Text>
          </Flex>
        ))}
      </HStack>

      {/* Meal detail */}
      {activeMeal && (
        <MealDetail
          meal={activeMeal}
          day={activeDay + 1}
          mealType={activeMealType}
          readOnly={readOnly}
          showNutritionInfo={showNutritionInfo}
        />
      )}
    </Flex>
  );
};
